import * as React from 'react';
import type { LoginLabels } from '../../organisms/login-form';
import { LoginForm } from '../../organisms/login-form';
import { ResetPassword } from '../reset-password';
import { LoginStepEnum, useLogin } from './useLogin';
import type { BrandLogo } from '@components/molecules/brand-header';
import { ChangeTemporaryPasswordForm } from '@components/organisms/change-temp-password-form';
import type { ChangeTemporaryPasswordFormLabels } from '@components/organisms/change-temp-password-form';
import type { SelectEmailFormLabels } from '@components/organisms/choose-email-form';
import type { ResetPasswordLabels } from '@components/organisms/reset-password-form';

type Props = {
	labels: {
		login: LoginLabels;
		resetPassword: ResetPasswordLabels;
		chooseEmail: SelectEmailFormLabels;
		changePassword: ChangeTemporaryPasswordFormLabels;
	};
	logo?: BrandLogo;
	tosLink?: string;
	onSuccess: (data: unknown) => Promise<void> | void;
	handleNavigateToLink?: () => void;
	onSSOSuccess: () => void | Promise<void>;
	onEmailChange?: (email: string) => void;
	belowSubmitButton?: {
		label: string;
		onClick: () => void;
		testId?: string;
	};
};

export const Login: React.FC<Props> = ({
	labels,
	logo,
	tosLink,
	onSuccess,
	handleNavigateToLink,
	onSSOSuccess,
	onEmailChange,
	belowSubmitButton,
}) => {
	const { step, handleLogin, changeStep, handleChangePassword, navigateToForgotPassword, loginEmail, errorCode } =
		useLogin({
			onSuccess,
		});

	const stickyLogoutError: string | undefined = labels.login.errors?.[errorCode!] ?? undefined;

	const CurrentStep: React.ReactNode = React.useMemo(() => {
		const goBackToLogin = () => changeStep(LoginStepEnum.LOGIN);

		switch (step) {
			case LoginStepEnum.LOGIN:
				return (
					<LoginForm
						tosLink={tosLink}
						labels={labels.login}
						logo={logo}
						onSubmit={handleLogin}
						handleNavigateToForgotPassword={navigateToForgotPassword}
						handleNavigateToLink={handleNavigateToLink}
						externalError={stickyLogoutError}
						onSSOSuccess={onSSOSuccess}
						onEmailChange={onEmailChange}
						belowSubmitButton={belowSubmitButton}
					/>
				);
			case LoginStepEnum.CHANGE_TEMP_PASSWORD:
				return <ChangeTemporaryPasswordForm labels={labels.changePassword} onSubmit={handleChangePassword} />;
			case LoginStepEnum.RESET_PASSWORD:
				return (
					<ResetPassword
						labels={{ chooseEmail: labels.chooseEmail, resetPassword: labels.resetPassword }}
						onPasswordReset={goBackToLogin}
						goBackToLogin={goBackToLogin}
						initialEmail={loginEmail}
					/>
				);
			default:
				return null;
		}
	}, [
		step,
		logo,
		handleLogin,
		handleNavigateToLink,
		handleChangePassword,
		changeStep,
		navigateToForgotPassword,
		onSSOSuccess,
		onEmailChange,
		belowSubmitButton,
		labels,
		tosLink,
		stickyLogoutError,
		loginEmail,
	]);

	return <div className="flex w-full flex-col justify-center">{CurrentStep}</div>;
};
